import { api } from './api';

export interface DSAProblem {
  id: string;
  title: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  pattern: string;
  category: string;
  leetcodeUrl: string;
  description: string;
  approach: string;
  timeComplexity: string;
  spaceComplexity: string;
  companies: string[];
  tags: string[];
  isPremium: boolean;
  isActive: boolean;
  hints?: string[];
  examples?: {
    input: string;
    output: string;
    explanation?: string;
  }[];
  constraints?: string;
  followUp?: string;
  solvedCount: number;
  attemptCount: number;
  upvotes: number;
  downvotes: number;
  categoryOrder?: number;
  orderInCategory?: number;
  createdAt: string;
  updatedAt: string;
}

export interface DSAFilter {
  patterns?: string[];
  difficulties?: string[];
  categories?: string[];
  companies?: string[];
  tags?: string[];
  search?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  questionSet?: number; // Filter by question set (75, 150, 300)
}

export interface DSAProblemWithProgress extends DSAProblem {
  progress?: {
    id: string;
    isCompleted: boolean;
    isFavorited: boolean;
    completedAt?: string;
    attempts: number;
    timeSpent: number;
    notes?: string;
    solution?: string;
    solutionLanguage?: string;
    lastAttemptAt?: string;
  };
}

export interface DSAStats {
  totalProblems: number;
  solvedProblems: number;
  easyProblems: { total: number; solved: number };
  mediumProblems: { total: number; solved: number };
  hardProblems: { total: number; solved: number };
  patternStats: Array<{
    pattern: string;
    total: number;
    solved: number;
    percentage: number;
  }>;
  categoryStats: Array<{
    category: string;
    total: number;
    solved: number;
    percentage: number;
  }>;
  recentActivity: Array<{
    problemId: string;
    title: string;
    difficulty: string;
    pattern: string;
    completedAt: string;
  }>;
  streakData: {
    currentStreak: number;
    bestStreak: number;
    weeklyProgress: number[];
  };
}

class DSAService {
  async getProblems(filter: DSAFilter = {}): Promise<{
    problems: DSAProblem[];
    total: number;
    page: number;
    limit: number;
  }> {
    const queryParams = new URLSearchParams();

    // Add filter parameters
    if (filter.patterns?.length) {
      queryParams.append('patterns', filter.patterns.join(','));
    }
    if (filter.difficulties?.length) {
      queryParams.append('difficulties', filter.difficulties.join(','));
    }
    if (filter.categories?.length) {
      queryParams.append('categories', filter.categories.join(','));
    }
    if (filter.companies?.length) {
      queryParams.append('companies', filter.companies.join(','));
    }
    if (filter.tags?.length) {
      queryParams.append('tags', filter.tags.join(','));
    }
    if (filter.search) {
      queryParams.append('search', filter.search);
    }
    if (filter.page) {
      queryParams.append('page', filter.page.toString());
    }
    if (filter.limit) {
      queryParams.append('limit', filter.limit.toString());
    }
    if (filter.sort) {
      queryParams.append('sort', filter.sort);
    }
    if (filter.order) {
      queryParams.append('order', filter.order);
    }
    if (filter.questionSet) {
      queryParams.append('questionSet', filter.questionSet.toString());
    }

    const query = queryParams.toString();
    return await api.get(`/dsa${query ? `?${query}` : ''}`);
  }

  async getProblemsWithProgress(filter: DSAFilter = {}): Promise<{
    problems: DSAProblemWithProgress[];
    total: number;
    page: number;
    limit: number;
  }> {
    const queryParams = new URLSearchParams();

    // Add filter parameters (same as above)
    if (filter.patterns?.length) {
      queryParams.append('patterns', filter.patterns.join(','));
    }
    if (filter.difficulties?.length) {
      queryParams.append('difficulties', filter.difficulties.join(','));
    }
    if (filter.categories?.length) {
      queryParams.append('categories', filter.categories.join(','));
    }
    if (filter.companies?.length) {
      queryParams.append('companies', filter.companies.join(','));
    }
    if (filter.tags?.length) {
      queryParams.append('tags', filter.tags.join(','));
    }
    if (filter.search) {
      queryParams.append('search', filter.search);
    }
    if (filter.page) {
      queryParams.append('page', filter.page.toString());
    }
    if (filter.limit) {
      queryParams.append('limit', filter.limit.toString());
    }
    if (filter.sort) {
      queryParams.append('sort', filter.sort);
    }
    if (filter.order) {
      queryParams.append('order', filter.order);
    }
    if (filter.questionSet) {
      queryParams.append('questionSet', filter.questionSet.toString());
    }

    const query = queryParams.toString();
    return await api.get(`/dsa/with-progress${query ? `?${query}` : ''}`);
  }

  async getProblem(id: string): Promise<DSAProblem> {
    return await api.get(`/dsa/${id}`);
  }

  async getUserStats(): Promise<DSAStats> {
    return await api.get('/dsa/stats');
  }

  async getPatterns(): Promise<string[]> {
    return await api.get('/dsa/patterns');
  }

  async getCategories(): Promise<string[]> {
    return await api.get('/dsa/categories');
  }

  async getCompanies(): Promise<string[]> {
    return await api.get('/dsa/companies');
  }

  async getTags(): Promise<string[]> {
    return await api.get('/dsa/tags');
  }

  // Create problem (admin only)
  async createProblem(problemData: Omit<DSAProblem, 'id' | 'createdAt' | 'updatedAt' | 'solvedCount' | 'attemptCount' | 'upvotes' | 'downvotes'>): Promise<DSAProblem> {
    return await api.post('/dsa', problemData);
  }

  // Update problem (admin only)
  async updateProblem(id: string, updates: Partial<DSAProblem>): Promise<DSAProblem> {
    return await api.patch(`/dsa/${id}`, updates);
  }

  // Delete problem (admin only)
  async deleteProblem(id: string): Promise<void> {
    return await api.delete(`/dsa/${id}`);
  }

  // Progress methods
  async updateProgress(problemId: string, progress: {
    isCompleted?: boolean;
    isFavorited?: boolean;
    timeSpent?: number;
    notes?: string;
  }): Promise<any> {
    return await api.patch(`/progress/${problemId}`, progress);
  }

  async getAllProgress(): Promise<any[]> {
    return await api.get('/progress/all');
  }

  async getProgress(problemId: string): Promise<any> {
    return await api.get(`/progress/${problemId}`);
  }

  async toggleFavorite(problemId: string): Promise<any> {
    return await api.post(`/progress/${problemId}/favorite`, {});
  }

  // Notes methods
  async getNotes(problemId: string): Promise<any> {
    return await api.get(`/dsa/notes/${problemId}`);
  }

  async createNotes(problemId: string, notes: string, notesFormat: 'markdown' | 'html' = 'markdown'): Promise<any> {
    return await api.post('/dsa/notes', {
      problemId,
      notes,
      notesFormat,
    });
  }

  async updateNotes(problemId: string, notes: string, notesFormat: 'markdown' | 'html' = 'markdown'): Promise<any> {
    return await api.patch(`/dsa/notes/${problemId}`, {
      notes,
      notesFormat,
    });
  }

  async deleteNotes(problemId: string): Promise<void> {
    return await api.delete(`/dsa/notes/${problemId}`);
  }
}

export const dsaService = new DSAService();
export default dsaService;
